// ABOUTME: Reusable button component to redirect users from old gnowbe-web to new gnowbe-frontend
// ABOUTME: Handles URL mapping to preserve user context when switching apps

import React from 'react'
import { Button } from 'components/utils/Button'
import { trackButtonEnhanced } from 'utils/track'

interface ExploreNewAppButtonProps {
  /** The screen/page name where the button is displayed (for analytics) */
  onScreen?: string
  /** Button type (default: 'gnowbe') */
  type?: 'gnowbe' | 'sub' | 'primary' | 'text' | 'guide' | 'danger' | 'team' | 'quote' | 'edit' | 'chat' | 'ad' | 'magic'
  /** Custom button text (default: 'Explore the New Web App') */
  text?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Unregisters every service worker for this origin and drops all Cache Storage
 * buckets. Best effort only — every failure is swallowed so the app flip is
 * never blocked by cleanup.
 */
async function evictServiceWorkers(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    }
    // Drop image-cache-v* too: entries are keyed by path and matched with
    // `ignoreSearch: true`, so stale bytes can be served for URLs the new app
    // requests with different signatures.
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // Never block the flip on cleanup failure.
  }
}

/**
 * Button that redirects users from the old gnowbe-web app to the new gnowbe-frontend app
 * while preserving their current context/view through URL mapping.
 * 
 * When clicked:
 * 1. Tracks analytics event
 * 2. Sets app_version=new cookie (2 year expiration)
 * 3. Evicts this app's service worker + caches (see evictServiceWorkers)
 * 4. Reloads — the server serves the new app for the same URL
 */
export const ExploreNewAppButton: React.FC<ExploreNewAppButtonProps> = ({
  onScreen = 'Unknown',
  type = 'gnowbe',
  text = 'Try Our New Beta App',
  className,
}) => {
  const handleClick = async () => {
    // Track button click for analytics
    trackButtonEnhanced({
      button: text,
      onScreen,
    })

    // Set the cookie FIRST — if the eviction below hangs or throws, the flip
    // must still land on the new app.
    const date = new Date()
    date.setTime(date.getTime() + (2 * 365 * 24 * 60 * 60 * 1000))
    document.cookie = `app_version=new; expires=${date.toUTCString()}; path=/`

    // Evict this app's service worker + caches before the reload.
    //
    // A plain reload does NOT release SW control of the document, so without
    // this the new app boots with sw.js still intercepting every cdn1.* request.
    // sw.js re-fetches those with `credentials: 'omit'`, which strips the
    // CloudFront signed cookies the new app authenticates media with → 403 on
    // every program image. unregister() only stops FUTURE documents from being
    // controlled, so it has to happen before the navigation, not after.
    await Promise.race([
      evictServiceWorkers(),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ])

    window.location.reload()
  }

  return (
    <Button
      type={type}
      text={text}
      onClick={handleClick}
      className={className}
    />
  )
}
